import { useFormik } from "formik";
import React, { Fragment, useEffect, useMemo } from "react";
import { FaEuroSign } from "react-icons/fa6";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";
import { Button, cn, FileInput, Input, Switch, Text, Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from "yup";
import ContractTemplateWYSIWYGEditor from "../../components/contract-templates/ContractTemplateWYSIWYGEditor";
import CustomSelect from "../../components/forms/CustomSelect";
import RequiredInput from "../../components/forms/InputHOC";
import { usePrivilege } from "../../components/priviledge/PriviledgeProvider";
import useCorporations from "../../hooks/api-calls/useCorporations";
import useProfessionalAreas from "../../hooks/api-calls/useProfessionalAreas";
import useProductTypes from "../../hooks/api-calls/useProductTypes";
import useTrainingActionGroups from "../../hooks/api-calls/useTrainingActionGroups";
import useTrainingModalities from "../../hooks/api-calls/useTrainingModalities";
import Page from "../../layout/Page/Page";
import FormGroup from "../../layout/shared/form-group";
import { DocumentsService } from "../../services/document/documentsService";
import { ProductTypeService } from "../../services/product-type/productTypeService";
import { ProductService } from "../../services/product/productService";
import { Product } from "../../type/entities/product-type";
import { convertEditorJSONToHTML, convertHTMLToEditorJSON, isValidHTML } from "../../utils/editorConversions";
import FormFooter from "../_layout/_footers/form-footer";
import ComplementsForm from "./components/ComplementsForm";
import ProductTutorsForm from "./components/ProductTutorsForm";
import { FaTrash } from "react-icons/fa";

/** Tipos de producto considerados formación. No incluir "Servicio". */
const TRAINING_PRODUCT_TYPES = ['Formación', 'Bonificada', 'CursoBonificado'];

/** Tipo de producto que muestra campos de Curso de Formación (FUNDAE). */
const CURSO_BONIFICADO_TYPE = 'CursoBonificado';
const SUBSIDISED_PRODUCT_TYPE_ID = '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1';

const FUNDAE_FIELDS = ['trainingActionGroupId', 'professionalAreaId', 'trainingModalityId', 'presentialHours', 'teleformHours', 'actionType', 'trainingLevel', 'objectives', 'contents'] as const;

const ACTION_TYPE_OPTIONS = [{ value: 'Genérica', label: 'Genérica' }, { value: 'Específica', label: 'Específica' }];
const TRAINING_LEVEL_OPTIONS = [{ value: 'Básica', label: 'Básica' }, { value: 'Superior', label: 'Superior' }];

const normalizeWorkersRanges = (ranges: any[]) =>
    (ranges || []).map((range: any) => ({
        workersFrom: Number(range.workersFrom),
        workersTo: Number(range.workersTo),
        amount: Number(range.amount),
    })).sort((currentRange, nextRange) => currentRange.workersFrom - nextRange.workersFrom);

const parseBooleanValue = (value: any): boolean => [true, 1, '1', 'true', 't'].includes(value);
const normalizeNullableText = (value: any): string | null => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized !== '' ? normalized : null;
};

type OrganisingCorporationPlatformDataRow = {
    organisingCorporationId: string;
    organisingCorporationName?: string;
    platformCif?: string | null;
    platformBusinessName?: string | null;
    platformUrl?: string | null;
};

interface CreateFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Product | undefined;
}

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo').required('Campo obligatorio'),
    productTypeId: yup.string().required('Campo obligatorio'),
    code: yup.string().min(1, 'Demasiado corto').max(200, 'Demasiado largo')
        .when('productTypeId', {
            is: SUBSIDISED_PRODUCT_TYPE_ID,
            then: schema => schema.required('Campo obligatorio para productos bonificados'),
            otherwise: schema => schema.notRequired().nullable(),
        }),
    weeklyHours: yup.number().min(0, 'Debe ser mayor que 0').notRequired().nullable(),
    amount: yup.number()
        .typeError('Debe ser un número válido')
        .positive('Debe ser un número positivo')
        .test(
            'max-2-decimals',
            'El número no puede tener más de 2 decimales',
            (value) => {
                if (value === undefined || value === null) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
        )
        .required('Campo obligatorio'),
    workersRangePriceEnabled: yup.boolean().notRequired(),
    workersRangeExtraPriceEnabled: yup.boolean().notRequired(),
    workersRangeExtraPricePerWorker: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
            return value;
        })
        .when(['workersRangePriceEnabled', 'workersRangeExtraPriceEnabled'], {
            is: (workersRangePriceEnabled: boolean, workersRangeExtraPriceEnabled: boolean) => workersRangePriceEnabled && workersRangeExtraPriceEnabled,
            then: schema => schema
                .typeError('Debe ser un número válido')
                .positive('Debe ser un número positivo')
                .test(
                    'max-2-decimals',
                    'El número no puede tener más de 2 decimales',
                    (value) => {
                        if (value === undefined || value === null) return true;
                        return /^\d+(\.\d{1,2})?$/.test(value.toString());
                    }
                )
                .required('Campo obligatorio'),
            otherwise: schema => schema.notRequired().nullable(),
        }),
    workersRangePrices: yup.array().of(yup.object().shape({
        workersFrom: yup
            .number()
            .typeError('Debe ser un número válido')
            .integer('Debe ser un número entero')
            .positive('Debe ser mayor que 0')
            .required('Campo obligatorio'),
        workersTo: yup
            .number()
            .typeError('Debe ser un número válido')
            .integer('Debe ser un número entero')
            .positive('Debe ser mayor que 0')
            .required('Campo obligatorio')
            .test('workers-range-order', 'Debe ser mayor o igual que el inicio', function(value) {
                const { workersFrom } = this.parent;
                if (value === undefined || value === null || workersFrom === undefined || workersFrom === null) return true;
                return Number(value) >= Number(workersFrom);
            }),
        amount: yup.number().typeError('Debe ser un número válido').positive('Debe ser un número positivo').test(
            'max-2-decimals',
            'El número no puede tener más de 2 decimales',
            (value) => {
                if (value === undefined || value === null) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
        ).required('Campo obligatorio'),
    }))
        .when('workersRangePriceEnabled', {
            is: true,
            then: schema => schema.min(1, 'Debes añadir al menos un rango de trabajadores').required('Debes añadir al menos un rango de trabajadores'),
            otherwise: schema => schema.notRequired(),
        })
        .test('workers-ranges-continuous', 'Los rangos deben ser continuos, sin huecos y sin solapamientos', function(value) {
            const workersRangePriceEnabled = this.parent.workersRangePriceEnabled;
            if (!workersRangePriceEnabled) return true;

            const ranges = normalizeWorkersRanges(value || []);
            if (ranges.length === 0) return true;
            if (ranges[0].workersFrom !== 1) return false;

            for (let index = 1; index < ranges.length; index++) {
                const currentRange = ranges[index];
                const previousRange = ranges[index - 1];

                if (currentRange.workersFrom <= previousRange.workersTo) return false;
                if (currentRange.workersFrom !== previousRange.workersTo + 1) return false;
            }

            return true;
        }),
    cost: yup.number()
        .typeError('Debe ser un número válido')
        .positive('Debe ser un número positivo')
        .test(
            'max-2-decimals',
            'El número no puede tener más de 2 decimales',
            (value) => {
                if (value === undefined || value === null) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
        )
        .required('Campo obligatorio'),
    complements: yup.array().of(yup.object().shape({
        productId: yup.string().required('Campo obligatorio'),
        name: yup.string().required('Campo obligatorio'),
        amount: yup.number().typeError('Debe ser un número válido').positive('Debe ser un número positivo').test(
            'max-2-decimals',
            'El número no puede tener más de 2 decimales',
            (value) => {
                if (value === undefined || value === null) return true;
                return /^\d+(\.\d{1,2})?$/.test(value.toString());
            }
        ).required('Campo obligatorio'),
    })),
    studentFieldsRequired: yup.boolean().notRequired(),
    consultancyFieldsRequired: yup.boolean().notRequired(),
    contractRequired: yup.boolean().notRequired(),
    contractTemplate: yup.string().nullable().notRequired(),
    score: yup.number().required('Campo obligatorio'),
    penaltyScore: yup.number().required('Campo obligatorio'),
    trainingActionGroupId: yup.string().nullable().notRequired(),
    professionalAreaId: yup.string().nullable().notRequired(),
    trainingModalityId: yup.string().nullable().notRequired(),
    presentialHours: yup.number().nullable().notRequired(),
    teleformHours: yup.number().nullable().notRequired(),
    actionType: yup.string().nullable().notRequired(),
    trainingLevel: yup.string().nullable().notRequired(),
    objectives: yup.string().nullable().notRequired(),
    contents: yup.string().nullable().notRequired(),
    organisingCorporationsPlatformData: yup.array().of(
        yup.object().shape({
            organisingCorporationId: yup.string().required('La organizadora es obligatoria'),
            platformCif: yup.string().nullable().notRequired(),
            platformBusinessName: yup.string().nullable().notRequired(),
            platformUrl: yup.string().nullable().notRequired(),
        })
    ).nullable().notRequired(),
});

const ProductForm: React.FC<CreateFormProps> = ({ isLoading, submit, data }) => {

    const { id = '' } = useParams<{ id: string }>();
    const { getProductTypesList } = useProductTypes({ active: true });
    const { getTrainingActionGroupsList, updateTrainingActionGroupsHookFilters } = useTrainingActionGroups();
    const { getProfessionalAreasList } = useProfessionalAreas();
    const { getTrainingModalitiesList } = useTrainingModalities();
    const { fetchCorporations } = useCorporations({ active: true, corporation_function: 'organising' });
    const mode = data ? 'Editar' : 'Crear';
    const navigate = useNavigate();
    const { userCan } = usePrivilege();

    const [complement, setComplement] = React.useState<{ productId: string, name: string, amount: string }>({ productId: '', name: '', amount: '' });
    const [variables, setVariables] = React.useState<any[]>([]);
    const [contractWatermarkFile, setContractWatermarkFile] = React.useState<File | null>(null);
    const [contractWatermarkPreviewUrl, setContractWatermarkPreviewUrl] = React.useState<string | null>(null);
    const [productTypeWatermarkPreviewUrl, setProductTypeWatermarkPreviewUrl] = React.useState<string | null>(null);
    const [removeContractWatermark, setRemoveContractWatermark] = React.useState(false);
    const loadDocumentPreview = async (documentId: string, setter: (value: string | null) => void) => {
        try {
            const response = await (await (new DocumentsService()).renderDocument(documentId)).getResponse();
            const file = new Blob([response.data], { type: response.headers?.['content-type'] || 'image/*' });
            setter(URL.createObjectURL(file));
        } catch (error) {
            console.error('Error al cargar la previsualización del documento:', error);
            setter(null);
        }
    };

    // Memoizar initialData para evitar re-renders del editor. Maneja tanto JSON como HTML del contractTemplate
    const memoizedInitialData = useMemo(() => {
        if (!data?.contractTemplate) return undefined;

        try {
            // Intenta parsear como JSON primero (formato legacy)
            return JSON.parse(data.contractTemplate);
        } catch {
            // Si falla, asume que es HTML y convierte a JSON
            if (isValidHTML(data.contractTemplate)) {
                return convertHTMLToEditorJSON(data.contractTemplate);
            }
            return undefined;
        }
    }, [data?.contractTemplate]);

    const loadTemplates = async () => {
        try {
            const response = await (new ProductService).getContractFields();
            const data = response.getResponseData()?.data;
            data && setVariables(data || []);
        } catch (error) {
            console.error('Error al cargar las variables:', error);
            toast.error('Error al cargar las variables');
        }
    };

    const formik = useFormik({
        initialValues: {
            productId: data?.id ?? '',
            name: data?.name ?? '',
            productTypeId: data?.productType?.id ?? SUBSIDISED_PRODUCT_TYPE_ID,
            code: data?.code ?? null,
            weeklyHours: data?.weeklyHours ?? null,
            amount: data?.amount ?? '',
            workersRangePriceEnabled: data?.workersRangePriceEnabled ?? (data as any)?.workers_range_price_enabled ?? false,
            workersRangeExtraPriceEnabled: parseBooleanValue(data?.workersRangeExtraPriceEnabled ?? (data as any)?.workers_range_extra_price_enabled ?? false),
            workersRangeExtraPricePerWorker: data?.workersRangeExtraPricePerWorker ?? (data as any)?.workers_range_extra_price_per_worker ?? '',
            workersRangePrices: (data?.workersRangePrices ?? (data as any)?.workers_range_prices ?? []).map((range: any) => ({
                workersFrom: range?.workersFrom ?? range?.workers_from ?? '',
                workersTo: range?.workersTo ?? range?.workers_to ?? '',
                amount: range?.amount ?? '',
            })),
            cost: data?.cost ?? 0,
            active: data?.active ?? true,
            description: data?.description ?? '',
            complements: data?.complements
                ? data.complements.map((p: any) => ({
                    productId: p.productComplement.id,
                    name: p.name,
                    amount: p.amount,
                    productName: p.productComplement.name, // This is used for the select input
                }))
                : [],
            studentFieldsRequired: data?.studentFieldsRequired ?? false,
            consultancyFieldsRequired: data?.consultancyFieldsRequired ?? false,
            contractRequired: data?.contractRequired ?? false,
            contractTemplate: data?.contractTemplate ?? null,
            score: data?.score ?? 0,
            penaltyScore: data?.penaltyScore ?? 0,
            trainingActionGroupId: data?.trainingActionGroup?.id ?? '',
            professionalAreaId: data?.professionalArea?.id ?? '',
            trainingModalityId: data?.trainingModality?.id ?? '',
            presentialHours: data?.presentialHours ?? null,
            teleformHours: data?.teleformHours ?? null,
            actionType: data?.actionType ?? null,
            trainingLevel: data?.trainingLevel ?? null,
            objectives: data?.objectives ?? '',
            contents: data?.contents ?? '',
            organisingCorporationsPlatformData: ((data as any)?.organisingCorporationsPlatformData ?? []).map(
                (row: OrganisingCorporationPlatformDataRow) => ({
                    organisingCorporationId: row.organisingCorporationId,
                    organisingCorporationName: row.organisingCorporationName ?? '',
                    platformCif: row.platformCif ?? '',
                    platformBusinessName: row.platformBusinessName ?? '',
                    platformUrl: row.platformUrl ?? '',
                })
            ),
            assignedTutors: ((data as any)?.assignedTutors ?? [])
                .slice()
                .sort((a: any, b: any) => (a.priority ?? 999) - (b.priority ?? 999))
                .map((row: any) => ({
                    productHasTutorId: row.id,
                    tutorId: row.tutor?.id ?? row.tutorId ?? '',
                    label: row.tutor ? `${row.tutor.name} ${row.tutor.lastName} (${row.tutor.nifCif})` : row.label ?? '',
                    tutorType: row.tutor?.tutorType ?? row.tutorType ?? '',
                })),
        },
        validationSchema: schema,
        validateOnBlur: false,
        // validateOnChange: false,
        onSubmit: values => {
            const productTypeName = getProductTypesList()?.find((p: any) => p.value === values.productTypeId)?.label ?? '';
            const payload = {
                ...values,
                trainingActionGroupId: values.trainingActionGroupId || null,
                professionalAreaId: values.professionalAreaId || null,
                trainingModalityId: values.trainingModalityId || null,
            };
            if (productTypeName !== CURSO_BONIFICADO_TYPE) {
                FUNDAE_FIELDS.forEach(field => delete payload[field]);
                (payload as Record<string, unknown>).organisingCorporationsPlatformData = [];
            } else {
                (payload as Record<string, unknown>).organisingCorporationsPlatformData = (
                    values.organisingCorporationsPlatformData || []
                ).map((row: OrganisingCorporationPlatformDataRow) => ({
                    organisingCorporationId: row.organisingCorporationId,
                    platformCif: normalizeNullableText(row.platformCif),
                    platformBusinessName: normalizeNullableText(row.platformBusinessName),
                    platformUrl: normalizeNullableText(row.platformUrl),
                }));
            }
            if (!['CursoBonificado', 'Bonificada'].includes(productTypeName)) {
                (payload as Record<string, unknown>).code = null;
            }

            if (!values.workersRangePriceEnabled) {
                payload.workersRangePrices = [];
                payload.workersRangeExtraPriceEnabled = false;
                payload.workersRangeExtraPricePerWorker = null;
            } else {
                payload.workersRangePrices = (values.workersRangePrices || []).map((range: any) => ({
                    workersFrom: Number(range.workersFrom),
                    workersTo: Number(range.workersTo),
                    amount: Number(range.amount),
                }));
                payload.workersRangeExtraPriceEnabled = parseBooleanValue(values.workersRangeExtraPriceEnabled ?? false);
                payload.workersRangeExtraPricePerWorker = parseBooleanValue(values.workersRangeExtraPriceEnabled ?? false)
                    ? Number(values.workersRangeExtraPricePerWorker)
                    : null;
            }

            (payload as Record<string, unknown>).assignedTutors = (values.assignedTutors || [])
                .filter((row: any) => row?.tutorId)
                .map((row: any) => ({ tutorId: row.tutorId }));

            submit({
                ...payload,
                contractWatermarkFile,
                removeContractWatermark,
            });
        },
    });

    const currentTemplateProductType = useMemo(() => {
        const selectedProductType = getProductTypesList()?.find((productType: any) => productType.value === formik.values.productTypeId);

        return {
            id: selectedProductType?.value ?? data?.productType?.id ?? formik.values.productTypeId ?? null,
            name: selectedProductType?.label ?? data?.productType?.name ?? null,
        };
    }, [data?.productType?.id, data?.productType?.name, formik.values.productTypeId, getProductTypesList]);

    useEffect(() => {
        formik.values.contractRequired && loadTemplates();
    }, [formik.values.contractRequired]);

    useEffect(() => {
        setContractWatermarkFile(null);
        setRemoveContractWatermark(false);

        if (data?.contractWatermarkDocument?.id) {
            loadDocumentPreview(data.contractWatermarkDocument.id, setContractWatermarkPreviewUrl);
        } else {
            setContractWatermarkPreviewUrl(null);
        }
    }, [data?.id]);

    useEffect(() => {
        let active = true;

        const loadSelectedProductTypeWatermark = async () => {
            const selectedProductTypeId = formik.values.productTypeId;

            if (!selectedProductTypeId) {
                setProductTypeWatermarkPreviewUrl(null);
                return;
            }

            if (data?.productType?.id === selectedProductTypeId) {
                if (data.productType.contractWatermarkDocument?.id) {
                    loadDocumentPreview(data.productType.contractWatermarkDocument.id, setProductTypeWatermarkPreviewUrl);
                } else {
                    setProductTypeWatermarkPreviewUrl(null);
                }
                return;
            }

            try {
                const response = await (new ProductTypeService()).getProductTypeById(selectedProductTypeId);
                const responseData = response.getResponseData();
                const documentId = responseData?.data?.contractWatermarkDocument?.id;

                if (!active) {
                    return;
                }

                if (documentId) {
                    loadDocumentPreview(documentId, setProductTypeWatermarkPreviewUrl);
                } else {
                    setProductTypeWatermarkPreviewUrl(null);
                }
            } catch (error) {
                if (active) {
                    setProductTypeWatermarkPreviewUrl(null);
                }
            }
        };

        loadSelectedProductTypeWatermark();

        return () => {
            active = false;
        };
    }, [formik.values.productTypeId, data?.productType?.id, data?.productType?.contractWatermarkDocument?.id]);

    useEffect(() => {
        updateTrainingActionGroupsHookFilters({
            professional_area_id: formik.values.professionalAreaId || null
        });
    }, [formik.values.professionalAreaId, updateTrainingActionGroupsHookFilters]);

    const hydrateOrganisingCorporationsPlatformData = async () => {
        const organisations = await fetchCorporations({});
        if (!organisations) return;

        const currentRows = (formik.values.organisingCorporationsPlatformData || []) as OrganisingCorporationPlatformDataRow[];
        const existingRowsMap = new Map<string, OrganisingCorporationPlatformDataRow>(
            currentRows.map((row): [string, OrganisingCorporationPlatformDataRow] => [
                row.organisingCorporationId,
                row,
            ])
        );

        const mergedRows = organisations.map((organisation: { value: string; label: string }) => {
            const existingRow = existingRowsMap.get(organisation.value);
            return {
                organisingCorporationId: organisation.value,
                organisingCorporationName: organisation.label,
                platformCif: existingRow?.platformCif ?? '',
                platformBusinessName: existingRow?.platformBusinessName ?? '',
                platformUrl: existingRow?.platformUrl ?? '',
            };
        });

        formik.setFieldValue('organisingCorporationsPlatformData', mergedRows);
    };

    useEffect(() => {
        let mounted = true;

        const loadOrganisingCorporations = async () => {
            if (!mounted) return;
            await hydrateOrganisingCorporationsPlatformData();
        };

        loadOrganisingCorporations();

        return () => {
            mounted = false;
        };
    }, [data?.id]);

    const getContent = () => {
        const productTypeName = getProductTypesList()?.find((p: any) => p.value === formik.values.productTypeId)?.label ?? '';
        const isTrainingProduct = TRAINING_PRODUCT_TYPES.includes(productTypeName);

        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                            <FormGroup
                                title="Información General"
                                description="Datos principales"
                                className='pt-3 pb-4'
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-4"
                            >
                                <RequiredInput
                                    id='name'
                                    label="Nombre"
                                    formik={formik}
                                />

                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        id={'productType'}
                                        label="Tipo de producto"
                                        value={{ value: formik.values.productTypeId, label: getProductTypesList()?.find((g: any) => g.value === formik.values.productTypeId)?.label }}
                                        options={getProductTypesList()}
                                        onChange={(e: any) => {
                                            const newProductTypeId = e?.value;
                                            const newProductTypeName = getProductTypesList()?.find((p: any) => p.value === newProductTypeId)?.label ?? '';
                                            formik.setFieldValue('productTypeId', newProductTypeId);
                                            if (newProductTypeName !== CURSO_BONIFICADO_TYPE) {
                                                formik.setFieldValue('trainingActionGroupId', '');
                                                formik.setFieldValue('professionalAreaId', '');
                                                formik.setFieldValue('trainingModalityId', '');
                                                formik.setFieldValue('presentialHours', null);
                                                formik.setFieldValue('teleformHours', null);
                                                formik.setFieldValue('actionType', null);
                                                formik.setFieldValue('trainingLevel', null);
                                                formik.setFieldValue('objectives', '');
                                                formik.setFieldValue('contents', '');
                                                formik.setFieldValue('organisingCorporationsPlatformData', []);
                                            } else if (!formik.values.organisingCorporationsPlatformData?.length) {
                                                hydrateOrganisingCorporationsPlatformData();
                                            }
                                        }}
                                        required
                                        error={formik.errors.productTypeId}
                                        isDisabled={!userCan('get_product_types_for_select', 'product_types')}
                                    />
                                    {!userCan('get_product_types_for_select', 'product_types') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los tipos de producto</span>
                                        </div>
                                    )}
                                </div>

                                <RequiredInput
                                    id='code'
                                    label="Código de Acción Formativa"
                                    className={`[&>label>span]:font-medium ${!(formik.values.productTypeId !== null && formik.values.productTypeId === SUBSIDISED_PRODUCT_TYPE_ID) ? 'hidden' : ''}`}
                                    inputClassName="text-sm"
                                    onChange={formik.handleChange}
                                    value={formik.values.code}
                                    error={formik.errors.code}
                                    required={formik.values.productTypeId !== null && formik.values.productTypeId === SUBSIDISED_PRODUCT_TYPE_ID}
                                />

                                <RequiredInput
                                    id='weeklyHours'
                                    type="number"
                                    step={0.01}
                                    label="Horas semanales"
                                    className={`[&>label>span]:font-medium ${!(formik.values.productTypeId !== null && formik.values.productTypeId === SUBSIDISED_PRODUCT_TYPE_ID) ? 'hidden' : ''}`}
                                    formik={formik}
                                    required={false}
                                />

                                <RequiredInput
                                    id='amount'
                                    type="number"
                                    step={0.01}
                                    label="Importe"
                                    suffix={<FaEuroSign />}
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='cost'
                                    type="number"
                                    step={0.01}
                                    label="Coste"
                                    suffix={<FaEuroSign />}
                                    formik={formik}
                                />

                                <div className="col-span-12 md:col-span-4 rounded-md border border-gray-200 p-4">
                                    <div className="flex flex-col gap-4">
                                        <Switch
                                            id='workersRangePriceEnabled'
                                            label="Precio por rango de trabajadores"
                                            checked={formik.values.workersRangePriceEnabled}
                                            onChange={() => {
                                                const nextEnabled = !formik.values.workersRangePriceEnabled;
                                                formik.setFieldValue('workersRangePriceEnabled', nextEnabled);

                                                if (!nextEnabled) {
                                                    formik.setFieldValue('workersRangeExtraPriceEnabled', false);
                                                    formik.setFieldValue('workersRangeExtraPricePerWorker', '');
                                                }
                                            }}
                                            switchKnobClassName='bg-secondary'
                                        />

                                        {formik.values.workersRangePriceEnabled && (
                                            <div className="space-y-3">
                                                {formik.values.workersRangePrices?.map((range: any, index: number) => (
                                                    <div key={index} className="rounded-md border border-gray-100 p-3 space-y-3">
                                                        <div className="grid grid-cols-12 gap-2 items-end">
                                                            <Input
                                                                type="number"
                                                                label="Desde"
                                                                id={`workersRangePrices.${index}.workersFrom`}
                                                                value={range.workersFrom}
                                                                onChange={(event: any) => formik.setFieldValue(`workersRangePrices[${index}].workersFrom`, event.target.value)}
                                                                error={(formik.errors.workersRangePrices as any)?.[index]?.workersFrom}
                                                                className="col-span-3"
                                                            />
                                                            <Input
                                                                type="number"
                                                                label="Hasta"
                                                                id={`workersRangePrices.${index}.workersTo`}
                                                                value={range.workersTo}
                                                                onChange={(event: any) => formik.setFieldValue(`workersRangePrices[${index}].workersTo`, event.target.value)}
                                                                error={(formik.errors.workersRangePrices as any)?.[index]?.workersTo}
                                                                className="col-span-3"
                                                            />
                                                            <Input
                                                                type="number"
                                                                step={0.01}
                                                                label="Precio base"
                                                                id={`workersRangePrices.${index}.amount`}
                                                                value={range.amount}
                                                                onChange={(event: any) => formik.setFieldValue(`workersRangePrices[${index}].amount`, event.target.value)}
                                                                error={(formik.errors.workersRangePrices as any)?.[index]?.amount}
                                                                className="col-span-4"
                                                                suffix={<FaEuroSign />}
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                className="col-span-2"
                                                                onClick={() => {
                                                                    const newRanges = [...formik.values.workersRangePrices];
                                                                    newRanges.splice(index, 1);
                                                                    formik.setFieldValue('workersRangePrices', newRanges);
                                                                }}
                                                            >
                                                                <FaTrash className="text-sm" />
                                                            </Button>
                                                        </div>

                                                    </div>
                                                ))}

                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => formik.setFieldValue('workersRangePrices', [...formik.values.workersRangePrices, { workersFrom: '', workersTo: '', amount: '' }])}
                                                >
                                                    Añadir rango
                                                </Button>

                                                <div className="grid grid-cols-12 gap-2 items-end rounded-md border border-dashed border-gray-200 p-3">
                                                    <div className="col-span-12 md:col-span-8">
                                                        <Switch
                                                            id='workersRangeExtraPriceEnabled'
                                                            label="Precio extra a partir del máximo"
                                                            checked={formik.values.workersRangeExtraPriceEnabled}
                                                            onChange={() => {
                                                                const nextEnabled = !formik.values.workersRangeExtraPriceEnabled;
                                                                formik.setFieldValue('workersRangeExtraPriceEnabled', nextEnabled);
                                                                if (!nextEnabled) {
                                                                    formik.setFieldValue('workersRangeExtraPricePerWorker', '');
                                                                }
                                                            }}
                                                            switchKnobClassName='bg-secondary'
                                                        />
                                                    </div>

                                                    {formik.values.workersRangeExtraPriceEnabled && (
                                                        <Input
                                                            type="number"
                                                            step={0.01}
                                                            label="Precio extra por trabajador"
                                                            id='workersRangeExtraPricePerWorker'
                                                            value={formik.values.workersRangeExtraPricePerWorker}
                                                            onChange={(event: any) => formik.setFieldValue('workersRangeExtraPricePerWorker', event.target.value)}
                                                            error={formik.errors.workersRangeExtraPricePerWorker as any}
                                                            className="col-span-12 md:col-span-4"
                                                            suffix={<FaEuroSign />}
                                                        />
                                                    )}
                                                </div>

                                                {typeof formik.errors.workersRangePrices === 'string' && (
                                                    <p className="text-red-500 text-sm">{formik.errors.workersRangePrices}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <Switch
                                    id={id}
                                    label={formik.values.active ? 'Activo' : 'Desactivado'}
                                    checked={formik.values.active}
                                    onChange={() => formik.setFieldValue('active', !formik.values.active)}
                                    className='md:mt-6'
                                    switchKnobClassName='bg-secondary'
                                />


                                <RequiredInput
                                    id='score'
                                    type="number"
                                    label="Puntuación"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='penaltyScore'
                                    type="number"
                                    label="Puntuación de Penalización"
                                    formik={formik}
                                />

                                <Switch
                                    id='studentFieldsRequired'
                                    label="Necesarios campos de empleado"
                                    checked={formik.values.studentFieldsRequired}
                                    onChange={() => formik.setFieldValue('studentFieldsRequired', !formik.values.studentFieldsRequired)}
                                    className='md:mt-6'
                                    switchKnobClassName='bg-secondary'
                                />

                                <Switch
                                    id='consultancyFieldsRequired'
                                    label="Necesarios campos de asesoría"
                                    checked={formik.values.consultancyFieldsRequired}
                                    onChange={() => formik.setFieldValue('consultancyFieldsRequired', !formik.values.consultancyFieldsRequired)}
                                    className='md:mt-6'
                                    switchKnobClassName='bg-secondary'
                                />

                                {userCan('manage_product_contract', 'products') && (
                                    <Switch
                                        id='contractRequired'
                                        label="Necesario contrato"
                                        checked={formik.values.contractRequired}
                                        onChange={() => formik.setFieldValue('contractRequired', !formik.values.contractRequired)}
                                        className='md:mt-6'
                                        switchKnobClassName='bg-secondary'
                                    />
                                )}
                            </FormGroup>

                            {productTypeName === CURSO_BONIFICADO_TYPE && (
                                <FormGroup
                                    title="Curso de Formación"
                                    description="Datos específicos de formación bonificada (FUNDAE)"
                                    className='pt-6'
                                    titleCols="@md:col-span-2"
                                    childCols="@md:col-span-10 md:grid-cols-4"
                                >
                                    <CustomSelect
                                        id='professionalAreaId'
                                        label="Área profesional"
                                        value={formik.values.professionalAreaId
                                            ? getProfessionalAreasList()?.find((item: any) => item.value === formik.values.professionalAreaId) || null
                                            : null}
                                        options={getProfessionalAreasList()}
                                        isClearable
                                        isSearchable
                                        onChange={(event: any) => {
                                            formik.setFieldValue('professionalAreaId', event?.value ?? '');
                                            formik.setFieldValue('trainingActionGroupId', '');
                                        }}
                                    />
                                    <CustomSelect
                                        id='trainingActionGroupId'
                                        label="Grupo de acciones formativas"
                                        value={formik.values.trainingActionGroupId
                                            ? getTrainingActionGroupsList()?.find((item: any) => item.value === formik.values.trainingActionGroupId) || null
                                            : null}
                                        options={getTrainingActionGroupsList()}
                                        isClearable
                                        isSearchable
                                        isDisabled={!formik.values.professionalAreaId}
                                        onChange={(event: any) => formik.setFieldValue('trainingActionGroupId', event?.value ?? '')}
                                    />
                                    <CustomSelect
                                        id='trainingModalityId'
                                        label="Modalidad"
                                        value={formik.values.trainingModalityId
                                            ? getTrainingModalitiesList()?.find((item: any) => item.value === formik.values.trainingModalityId) || null
                                            : null}
                                        options={getTrainingModalitiesList()}
                                        isClearable
                                        isSearchable
                                        onChange={(event: any) => formik.setFieldValue('trainingModalityId', event?.value ?? '')}
                                    />
                                    <RequiredInput
                                        id='presentialHours'
                                        type="number"
                                        step={0.01}
                                        label="Horas presenciales"
                                        formik={formik}
                                        required={false}
                                    />
                                    <RequiredInput
                                        id='teleformHours'
                                        type="number"
                                        step={0.01}
                                        label="Horas teleformación"
                                        formik={formik}
                                        required={false}
                                    />
                                    <CustomSelect
                                        id='actionType'
                                        label="Tipo de acción"
                                        value={formik.values.actionType ? { value: formik.values.actionType, label: formik.values.actionType } : null}
                                        options={ACTION_TYPE_OPTIONS}
                                        isClearable
                                        onChange={(e: any) => formik.setFieldValue('actionType', e?.value ?? null)}
                                    />
                                    <CustomSelect
                                        id='trainingLevel'
                                        label="Nivel de formación"
                                        value={formik.values.trainingLevel ? { value: formik.values.trainingLevel, label: formik.values.trainingLevel } : null}
                                        options={TRAINING_LEVEL_OPTIONS}
                                        isClearable
                                        onChange={(e: any) => formik.setFieldValue('trainingLevel', e?.value ?? null)}
                                    />
                                    <Textarea
                                        id='objectives'
                                        label="Objetivos"
                                        className="[&>label>span]:font-medium lg:col-span-4"
                                        onChange={formik.handleChange}
                                        value={formik.values.objectives}
                                        error={formik.errors.objectives}
                                    />
                                    <Textarea
                                        id='contents'
                                        label="Contenidos"
                                        className="[&>label>span]:font-medium lg:col-span-4"
                                        onChange={formik.handleChange}
                                        value={formik.values.contents}
                                        error={formik.errors.contents}
                                    />
                                </FormGroup>
                            )}

                            {productTypeName === CURSO_BONIFICADO_TYPE && (
                                <FormGroup
                                    title="Plataforma Inspectora"
                                    description="Define los datos de plataforma por cada entidad organizadora. El usuario y contraseña inspector se generan automáticamente al crear."
                                    className='pt-6'
                                    titleCols="@md:col-span-2"
                                    childCols="@md:col-span-10"
                                >
                                    <div className="col-span-full space-y-3">
                                        {formik.values.organisingCorporationsPlatformData?.length ? (
                                            formik.values.organisingCorporationsPlatformData.map((row: OrganisingCorporationPlatformDataRow, index: number) => (
                                                <div key={row.organisingCorporationId} className="rounded-lg border border-gray-200 p-4">
                                                    <div className="grid grid-cols-12 gap-3">
                                                        <Input
                                                            label="Entidad organizadora"
                                                            value={row.organisingCorporationName || ''}
                                                            disabled
                                                            className="col-span-12 md:col-span-4"
                                                        />
                                                        <Input
                                                            label="CIF plataforma"
                                                            value={row.platformCif ?? ''}
                                                            onChange={(event: any) => formik.setFieldValue(
                                                                `organisingCorporationsPlatformData.${index}.platformCif`,
                                                                event.target.value
                                                            )}
                                                            className="col-span-12 md:col-span-4"
                                                        />
                                                        <Input
                                                            label="Razón social plataforma"
                                                            value={row.platformBusinessName ?? ''}
                                                            onChange={(event: any) => formik.setFieldValue(
                                                                `organisingCorporationsPlatformData.${index}.platformBusinessName`,
                                                                event.target.value
                                                            )}
                                                            className="col-span-12 md:col-span-4"
                                                        />
                                                        <Input
                                                            label="URL plataforma"
                                                            value={row.platformUrl ?? ''}
                                                            onChange={(event: any) => formik.setFieldValue(
                                                                `organisingCorporationsPlatformData.${index}.platformUrl`,
                                                                event.target.value
                                                            )}
                                                            className="col-span-12"
                                                        />
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">No hay organizadoras activas disponibles para esta empresa.</p>
                                        )}
                                    </div>
                                </FormGroup>
                            )}

                            {formik.values.contractRequired && (
                                <FormGroup
                                    title="Plantilla de Contrato"
                                    description="Editor de plantilla personalizada para este producto"
                                    className='pt-6'
                                    titleCols="@md:col-span-2"
                                    childCols="@md:col-span-10"
                                >
                                    <div className="col-span-full space-y-4">
                                        <div className="space-y-3 rounded-md border border-gray-200 p-4">
                                            <div className="space-y-1">
                                                <Text className="text-sm font-medium text-gray-900">Marca de agua</Text>
                                                <Text className="text-xs text-gray-500">
                                                    Si se configura, tendrá prioridad sobre la del tipo de producto y se aplicará en cada hoja del contrato.
                                                </Text>
                                            </div>

                                            <FileInput
                                                accept="image/*"
                                                placeholder="Seleccionar imagen de marca de agua"
                                                onChange={(event: React.ChangeEvent<any>) => {
                                                    const file = event.target.files?.[0];
                                                    if (!file) {
                                                        return;
                                                    }

                                                    setContractWatermarkFile(file);
                                                    setRemoveContractWatermark(false);
                                                    setContractWatermarkPreviewUrl(URL.createObjectURL(file));
                                                    event.target.value = '';
                                                }}
                                            />

                                            {contractWatermarkPreviewUrl && (
                                                <div className="space-y-3">
                                                    <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4">
                                                        <img
                                                            src={contractWatermarkPreviewUrl}
                                                            alt="Previsualización de marca de agua"
                                                            className="mx-auto max-h-40 w-auto object-contain opacity-70"
                                                        />
                                                    </div>

                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        color="danger"
                                                        onClick={() => {
                                                            setContractWatermarkFile(null);
                                                            setContractWatermarkPreviewUrl(null);
                                                            setRemoveContractWatermark(true);
                                                        }}
                                                    >
                                                        Quitar marca de agua
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        <ContractTemplateWYSIWYGEditor
                                            type='contract'
                                            initialData={memoizedInitialData}
                                            onChange={(data) => formik.setFieldValue('contractTemplate', convertEditorJSONToHTML(data))}
                                            placeholder="Maqueta aquí la plantilla de contrato para este producto..."
                                            variables={variables}
                                            productType={currentTemplateProductType}
                                            watermarkImage={contractWatermarkPreviewUrl ?? productTypeWatermarkPreviewUrl ?? null}
                                        />
                                        {formik.errors.contractTemplate && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {formik.errors.contractTemplate}
                                            </p>
                                        )}
                                    </div>
                                </FormGroup>
                            )}

                            {isTrainingProduct && (
                                <ProductTutorsForm formik={formik} productId={data?.id ?? null} />
                            )}

                            {userCan('admin_complements', 'complements') && (
                                <ComplementsForm
                                    formik={formik}
                                    complement={complement}
                                    setComplement={setComplement}
                                />
                            )}

                            <FormGroup
                                title="Descripción"
                                description="Información adicional"
                                className='pt-6'
                                titleCols="@md:col-span-2"
                                childCols="@md:col-span-10 md:grid-cols-4"
                            >
                                <Textarea
                                    id='description'
                                    className="[&>label>span]:font-medium lg:col-span-4"
                                    onChange={formik.handleChange}
                                    value={formik.values.description}
                                    error={formik.errors.description}
                                />
                            </FormGroup>
                        </div>
                    </form>
                </div>
            </>
        )
    };

    return (
        <Fragment>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Producto'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </Fragment>
    )
};

export default ProductForm;
